import { Pressable, StyleSheet, View } from 'react-native'

import { FigmaCheckIcon } from '../icons/FigmaSystemIcons'
import { Text, useTheme } from '../theme/restyle'
import { KYC_FORM_METRICS } from './kycFormTheme'

export interface FiatChoiceListProps {
  value: string
  options: readonly { id: string; labelFallback: string }[]
  editable: boolean
  invalid: boolean
  onChange: (value: string) => void
}

/**
 * The questionnaire's answers, drawn on the page itself (2475:9039).
 *
 * The country chooser keeps its sheet, since a list of every country is too
 * long to stand on a page. A questionnaire is nine answers, so the design
 * spends the page on them rather than a row that opens a sheet over it.
 */
export function FiatChoiceList({
  value,
  options,
  editable,
  invalid,
  onChange,
}: FiatChoiceListProps) {
  const theme = useTheme()
  const fill = invalid ? theme.form.fieldInvalid.fill : theme.form.field
  const label = invalid
    ? theme.form.fieldInvalid.content
    : theme.content.elements.primary
  return (
    <View style={[styles.card, { backgroundColor: fill }]}>
      {options.map((option, index) => (
        <View key={option.id}>
          {index === 0 ? null : (
            <View
              style={[styles.divider, { backgroundColor: theme.form.divider }]}
            />
          )}
          <Pressable
            accessibilityRole="radio"
            accessibilityLabel={option.labelFallback}
            accessibilityState={{
              selected: option.id === value,
              disabled: !editable,
            }}
            disabled={!editable}
            onPress={() => onChange(option.id)}
            style={styles.option}
          >
            <Text style={[styles.optionLabel, { color: label }]}>
              {option.labelFallback}
            </Text>
            {/* Drawn on every row, blank where it is not worn: the design
                reserves the slot throughout, and a mark that appears only on
                the answer would reflow the label as it is picked. */}
            <View style={option.id === value ? undefined : styles.unworn}>
              <FigmaCheckIcon
                size={KYC_FORM_METRICS.choiceCheckSize}
                color={theme.background.button.primary.default}
              />
            </View>
          </Pressable>
        </View>
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  // The card rounds and clips its rows, so no row draws a corner of its own.
  card: {
    borderRadius: KYC_FORM_METRICS.choiceListRadius,
    overflow: 'hidden',
  },
  option: {
    minHeight: KYC_FORM_METRICS.choiceRowHeight,
    paddingHorizontal: KYC_FORM_METRICS.choiceRowPaddingHorizontal,
    // Both of the design's row heights come from this one rule: a single line
    // falls under the 52 minimum, and a wrapped one makes the 64 it draws.
    paddingVertical: KYC_FORM_METRICS.choiceRowPaddingVertical,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  optionLabel: {
    flex: 1,
    fontSize: KYC_FORM_METRICS.choiceLabelFontSize,
    lineHeight: KYC_FORM_METRICS.choiceLabelLineHeight,
    letterSpacing: -0.3,
  },
  unworn: { opacity: 0 },
  divider: {
    height: 1,
    marginHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
  },
})
